import React from "react";
import { Alert, AlertTitle, Box, Chip, Stack, Typography } from "@mui/material";
import ConfidenceMeter from "./ConfidenceMeter";
import ConvoTraceBreakdown from "./ConvoTraceBreakdown";

const OUTCOME_COLOR = { matched: "success", clarified: "info", bricked: "error" };

// Coarse label of which signal produced the answer.
const matchTypeOf = (trace) => {
  const stages = trace.stages || {};
  if (Array.isArray(stages.exact) && stages.exact.length > 0) return "exact";
  if (stages.skip_terms?.triggered) return "semantic";
  const fusion = Array.isArray(stages.fusion) ? stages.fusion : [];
  return fusion.length > 0 ? fusion[0].source : null;
};

// One response's explain panel: outcome + score + the core-derived insight up
// top, a confidence meter, then the collapsible technical breakdown. The
// insight text and overlap analysis come from the core trace (trace.insight);
// this file only lays them out.
const ConvoTrace = (props) => {
  // inset=false drops the convo-history indent so the same panel fits inside a
  // side-by-side compare column.
  const { trace, expanded = false, inset = true } = props;
  if (!trace) return null;

  const insight = trace.insight || { severity: "info", title: trace.outcome || "result", body: "" };
  const type = matchTypeOf(trace);

  return (
    <Box
      sx={{
        ml: inset ? 7 : 0,
        mr: inset ? 2 : 0,
        mb: 1.5,
        px: 1.5,
        py: 1,
        bgcolor: "action.hover",
        borderRadius: 1,
        borderLeft: "3px solid",
        borderColor: `${OUTCOME_COLOR[trace.outcome] || "grey.400"}.main`,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
        <Chip size="small" color={OUTCOME_COLOR[trace.outcome] || "default"} label={trace.outcome || "unknown"} />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Score: {trace.score ?? "—"}
        </Typography>
        {type && (
          <Chip
            size="small"
            variant="outlined"
            color={type === "lexical" ? "warning" : "primary"}
            label={`${type} match`}
          />
        )}
      </Stack>

      <Alert severity={insight.severity} sx={{ mt: 1, py: 0.25, alignItems: "flex-start" }}>
        <AlertTitle sx={{ fontSize: "0.8rem", fontWeight: 700, mb: 0.25 }}>{insight.title}</AlertTitle>
        <Typography variant="caption">{insight.body}</Typography>
      </Alert>

      <Box sx={{ mt: 1 }}>
        <ConfidenceMeter trace={trace} />
      </Box>

      <ConvoTraceBreakdown trace={trace} expanded={expanded} />
    </Box>
  );
};

export default ConvoTrace;
